import React, { useState } from 'react';
import { CommissionTier } from '../../../types/commissionTier';
import { formatCurrency } from '../../../utils/formatters';
import { calculateTierCommission } from '../../../utils/commissionTierUtils';

interface Props {
  tiers: CommissionTier[];
  activeIdx: number;
  onSelectTier: (idx: number) => void;
}

export const BrokerCommissionTiersTable: React.FC<Props> = ({
  tiers,
  activeIdx,
  onSelectTier,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  if (!tiers || tiers.length === 0) return null;

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200/80 p-3 sm:p-4 shadow-sm space-y-2.5">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded((prev) => !prev);
        }}
      >
        <div className="flex items-center gap-1.5 sm:gap-2">
          <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
          <h5 className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-slate-700">
            Comisión Inicial y Tramos
          </h5>
        </div>
        <span className="text-[9px] sm:text-[10px] font-bold text-sky-600 bg-sky-50 px-2 py-0.5 rounded shrink-0">
          {isExpanded ? 'Ocultar' : `Ver ${tiers.length} opciones`}
        </span>
      </div>

      {isExpanded && (
        <div className="border border-slate-100 rounded-xl overflow-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <table className="table-fixed w-full text-left border-collapse text-[10px] sm:text-[11px]">
            <thead>
              <tr className="bg-slate-50 text-slate-500 font-black uppercase text-[8.5px] sm:text-[9px] tracking-wider border-b border-slate-100">
                <th className="w-[48%] py-1.5 sm:py-2 px-1.5 sm:px-2.5">Precio Venta</th>
                <th className="w-[16%] py-1.5 sm:py-2 px-1 text-center">%</th>
                <th className="w-[36%] py-1.5 sm:py-2 px-1.5 sm:px-2.5 text-right">Comisión</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier, idx) => {
                const isSelected = idx === activeIdx % tiers.length;
                const isInitial = Boolean(tier.es_inicial || idx === 0);
                const commVal = tier.valor_comision || calculateTierCommission(tier.precio, tier.porcentaje);
                return (
                  <tr
                    key={tier.id || idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectTier(idx);
                    }}
                    className={`cursor-pointer transition-colors border-b border-slate-50 ${
                      isSelected
                        ? isInitial
                          ? 'bg-indigo-50/80 text-indigo-950 font-bold'
                          : 'bg-emerald-50 text-emerald-950 font-bold'
                        : 'hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <td className="py-1.5 sm:py-2 px-1.5 sm:px-2.5 font-mono overflow-hidden">
                      <div className="flex items-center gap-1 sm:gap-1.5 truncate">
                        {isSelected && (
                          <span
                            className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full shrink-0 ${
                              isInitial ? 'bg-indigo-600' : 'bg-emerald-600'
                            }`}
                          />
                        )}
                        <span className="truncate">{formatCurrency(tier.precio)}</span>
                        <span
                          className={`text-[7.5px] sm:text-[8px] font-black uppercase px-1 py-0.2 rounded shrink-0 ${
                            isInitial
                              ? 'bg-indigo-100/70 text-indigo-700'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {isInitial ? 'Inicial' : 'Extra'}
                        </span>
                      </div>
                      {tier.descripcion && (
                        <span className="block text-[8px] sm:text-[9px] text-slate-400 font-sans font-normal truncate ml-2">
                          {tier.descripcion}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 sm:py-2 px-1 text-center font-bold text-sky-700">
                      {tier.porcentaje}%
                    </td>
                    <td
                      className={`py-1.5 sm:py-2 px-1.5 sm:px-2.5 text-right font-mono font-black truncate ${
                        isInitial ? 'text-indigo-700' : 'text-emerald-700'
                      }`}
                    >
                      {formatCurrency(commVal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
